import React from 'react';
import { isTouchDevice } from '../../utils/device';
import { useAuth } from '../../components/AuthContext';
import {NavLink, Link, Link as RouterLink, useNavigate, useLocation} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Drawer,
    Toolbar,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    IconButton,
    useMediaQuery,
    Typography, Breadcrumbs,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField,
    Alert,
    Paper
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArticleIcon from '@mui/icons-material/Article';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import NotesIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Groups';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import QuizIcon from '@mui/icons-material/Quiz';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';

const drawerWidth = 260;

const BOTTOM_NAV_HEIGHT = 64;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'sidebarOpen' })(({ theme }) => ({
  flexGrow: 1,
  padding: 0, // No padding - full width to match header
  paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom))`,
  minHeight: 'calc(100vh - 64px)',
  overflowX: 'hidden',
  position: 'relative',
  // Background gradient that flows from header
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(180deg, rgba(102,126,234,0.06) 0%, rgba(30,41,59,0.3) 50px, transparent 150px)'
    : 'linear-gradient(180deg, rgba(102,126,234,0.05) 0%, rgba(245,243,255,0.4) 40px, transparent 120px)',
  [theme.breakpoints.down('md')]: {
    paddingBottom: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom) + ${theme.spacing(1)})`,
  },
}));

// Glassmorphism style helpers
const glassStyles = {
  sidebar: (isDark) => ({
    background: isDark
      ? 'linear-gradient(180deg, rgba(30,41,59,0.95) 0%, rgba(17,24,39,0.92) 100%)'
      : 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(249,250,251,0.88) 100%)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRight: isDark ? '1px solid rgba(102,126,234,0.12)' : '1px solid rgba(99,102,241,0.1)',
    boxShadow: isDark
      ? '4px 0 20px rgba(0,0,0,0.2), inset -1px 0 0 rgba(102,126,234,0.08)'
      : '4px 0 20px rgba(102,126,234,0.06), inset -1px 0 0 rgba(102,126,234,0.05)',
  }),
  navItem: (isDark) => ({
    mx: 1, my: 0.3, borderRadius: 2,
    transition: 'all 200ms ease',
    '& .MuiListItemIcon-root': { color: isDark ? 'rgba(255,255,255,0.55)' : 'text.secondary', minWidth: 38 },
    '& .MuiListItemText-primary': { fontSize: 13.5, fontWeight: 500 },
    '&:hover': {
      bgcolor: isDark ? 'rgba(102,126,234,0.1)' : 'rgba(102,126,234,0.06)',
      transform: 'translateX(4px)',
    },
    '&.active': {
      background: isDark
        ? 'linear-gradient(135deg, rgba(102,126,234,0.2) 0%, rgba(118,75,162,0.15) 100%)'
        : 'linear-gradient(135deg, rgba(102,126,234,0.12) 0%, rgba(118,75,162,0.08) 100%)',
      backdropFilter: 'blur(8px)',
      border: '1px solid',
      borderColor: isDark ? 'rgba(102,126,234,0.25)' : 'rgba(102,126,234,0.2)',
      boxShadow: isDark ? '0 4px 16px rgba(102,126,234,0.15)' : '0 4px 16px rgba(102,126,234,0.12)',
      '& .MuiListItemIcon-root': { color: '#667eea' },
      '& .MuiListItemText-primary': { fontWeight: 700, color: isDark ? '#c7d2fe' : '#312e81' },
    },
  }),
  sectionHeader: (isDark) => ({
    fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    color: isDark ? 'rgba(102,126,234,0.55)' : 'rgba(102,126,234,0.55)',
    px: 2, pt: 1, pb: 0.25, userSelect: 'none',
  }),
};

// Reusable nav item to eliminate duplication
function SidebarNavItem({ to, icon, label, onClick, isDark }) {
  return (
    <ListItem disablePadding>
      <ListItemButton onClick={onClick} component={NavLink} to={to} sx={glassStyles.navItem(isDark)}>
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={label} />
      </ListItemButton>
    </ListItem>
  );
}

export default function DashboardLayout({ children, title, description, breadcrumbs = [], sx }) {
  React.useEffect(() => {
    try {
      document.body.classList.add('has-sidebar');
      document.body.style.setProperty('--sidebar-width', '260px');
    } catch {}
    return () => {
      try {
        document.body.classList.remove('has-sidebar');
        document.body.style.removeProperty('--sidebar-width');
      } catch {}
    };
  }, []);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [moreOpen, setMoreOpen] = React.useState(false);
  const { t } = useTranslation();
  const { user } = useAuth();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();

  // Close bottom sheet on route change
  React.useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  // Video call dialog state
  const [callDialogOpen, setCallDialogOpen] = React.useState(false);
  const [callMode, setCallMode] = React.useState('create'); // 'create' | 'join'
  const [groupName, setGroupName] = React.useState('');
  const [roomCode, setRoomCode] = React.useState('');

  const slugify = React.useCallback((s) => {
    return String(s || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }, []);

  const genRoomId = React.useCallback((name) => {
    const slug = slugify(name || 'meeting');
    const rand = Math.random().toString(36).slice(2, 6);
    return `${slug}-${rand}-${Date.now().toString(36)}`;
  }, [slugify]);

  const startVideoCall = React.useCallback(() => {
    // Navigate to Call page where user can create or join without a dialog
    try {
      if (!isMdUp) setMoreOpen(false);
      navigate('/call');
    } catch (_) {
      navigate('/call');
    }
  }, [isMdUp, navigate]);

  const handleCreateMeeting = React.useCallback(() => {
    const name = groupName.trim();
    if (!name) return; // keep dialog open until valid
    const roomId = genRoomId(name);
    if (!isMdUp) setMoreOpen(false);
    setCallDialogOpen(false);
    navigate(`/call/${roomId}`,{ state: { groupName: name } });
  }, [groupName, genRoomId, isMdUp, navigate]);

  const handleJoinMeeting = React.useCallback(() => {
    const input = roomCode.trim();
    if (!input) return; // keep dialog open until valid

    // Determine a safe base origin for URL parsing in all environments
    const PUBLIC_BASE = (process.env.REACT_APP_PUBLIC_BASE_URL || '').replace(/\/+$/, '');
    const baseOrigin = PUBLIC_BASE || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');

    let roomId = '';
    let groupName = '';
    try {
      const url = new URL(input, baseOrigin);
      const path = url.pathname || '';
      // If the path contains /call/<id>, extract that id; otherwise use the first non-empty segment as the id
      const m = path.match(/\/call\/([^\/?#]+)/);
      if (m && m[1]) {
        roomId = decodeURIComponent(m[1]);
      } else {
        const seg = path.replace(/^\/+/, '');
        roomId = seg || input.split('?')[0];
      }
      const g = url.searchParams.get('group');
      if (g) groupName = g;
    } catch (_) {
      // Not a URL; accept plain code or code with ?group=...
      const [codeOnly, query] = input.split('?');
      roomId = codeOnly;
      if (query) {
        const params = new URLSearchParams(query);
        const g = params.get('group');
        if (g) groupName = g;
      }
    }

    if (!roomId) return;

    if (!isMdUp) setMoreOpen(false);
    setCallDialogOpen(false);

    const search = groupName ? `?group=${encodeURIComponent(groupName)}` : '';
    navigate(`/call/${roomId}${search}`, groupName ? { state: { groupName } } : undefined);
  }, [roomCode, isMdUp, navigate]);

  const pageTitle = React.useMemo(() => {
    const p = location.pathname || '';
    if (p === '/' || p === '') return '';
    if (p.startsWith('/subjects')) return t('nav.subjects', 'Subjects');
    if (p.startsWith('/planning') || p.startsWith('/goals')) return t('planning.title', 'Personal Planning');
    if (p.startsWith('/points')) return t('points.title', 'Points');
    if (p.startsWith('/notes')) return t('nav.notes', 'Notes');
    if (p.startsWith('/self-tests')) return t('nav.selfTests', 'Self Tests');
    if (p.startsWith('/blog') || p.startsWith('/b/')) return t('nav.blog', 'Articles');
    if (p.startsWith('/groups')) return t('nav.groups', 'Groups');
    if (p.startsWith('/profile')) return t('common.profile', 'Profile');
    if (p.startsWith('/account/membership')) return t('membership.title', 'Membership');
    if (p.startsWith('/account/payments/methods')) return t('membership.managePayment', 'Manage payment method');
    if (p.startsWith('/account/payments/redeem')) return t('payments.picker.redeemLink', 'Redeem gift or promo code');
    if (p.startsWith('/account/payments/history')) return t('membership.viewHistory', 'Payment history');
    if (p.startsWith('/checkout')) return t('payment.chooseMethodTitle', 'Choose payment method');
    return '';
  }, [location.pathname, t]);

  // Determine whether user can see premium nav (Self Test, Groups)
  const plan = user?.planType ? String(user.planType) : '';
  const subscribedUntil = user?.subscribedUntil ? new Date(user.subscribedUntil) : null;
  const endOfDay = React.useCallback((d) => {
    if (!d || isNaN(d.getTime())) return null;
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  }, []);
  const endFuture = (() => {
    const e = subscribedUntil ? endOfDay(subscribedUntil) : null;
    return !!(e && e.getTime() >= Date.now());
  })();
  const isPaidActive = (plan === 'PAID_MONTHLY' || plan === 'PAID_YEARLY') && endFuture;

  // Consider an active FREE window when the backend grants a FREE plan with a subscribedUntil in the future
  const activeFreeWindow = plan === 'FREE' && endFuture;

  // Show premium menus only for paid users or users with an active FREE window
  const canSeePremiumMenus = isPaidActive || activeFreeWindow;

  // Days left until subscription end (ceil so same-day => 0)
  const daysLeft = React.useMemo(() => {
    try {
      if (!subscribedUntil || isNaN(subscribedUntil.getTime())) return null;
      const e = endOfDay(subscribedUntil);
      if (!e) return null;
      const ms = e.getTime() - Date.now();
      return Math.ceil(ms / (24 * 60 * 60 * 1000));
    } catch (_) { return null; }
  }, [subscribedUntil, endOfDay]);
  const showExpiryWarn = !!(subscribedUntil && !isNaN(subscribedUntil.getTime()) && (isPaidActive || activeFreeWindow) && typeof daysLeft === 'number' && daysLeft <= 3 && daysLeft >= 0);

  const effectiveTitle = title ?? pageTitle;
  const effectiveBreadcrumbs = React.useMemo(() => {
    if (Array.isArray(breadcrumbs) && breadcrumbs.length > 0) return breadcrumbs;
    const base = [{ label: t('nav.home', 'Home'), to: '/' }];
    if (effectiveTitle) base.push({ label: effectiveTitle });
    return base;
  }, [breadcrumbs, effectiveTitle, t]);

  const handleNavClick = React.useCallback(() => {
    if (!isMdUp) setMoreOpen(false);
  }, [isMdUp]);

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Logo */}
      <Box
        component={Link}
        to="/"
        onClick={handleNavClick}
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textDecoration: 'none',
          color: 'inherit',
          px: 1.5,
          py: 1,
          position: 'relative',
          transition: 'filter 200ms ease',
          '&:hover': { filter: 'brightness(1.05)' },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0, left: 0, right: 0, height: '3px',
            background: 'linear-gradient(90deg, #667eea, #764ba2)',
            opacity: 0.7,
            boxShadow: '0 0 12px rgba(102,126,234,0.4), 0 0 24px rgba(118,75,162,0.2)',
          },
        }}
        aria-label="Go to home"
      >
        <Box
          component="img"
          src="/icons/Edumee.jpeg"
          alt={t('brand.logoAlt', 'Logo')}
          sx={{
            width: '100%',
            maxHeight: { xs: 60, sm: 78, md: 88 },
            objectFit: 'contain',
            pointerEvents: 'none',
          }}
        />
      </Box>

      {/* Grouped Navigation */}
      <List dense={!isMdUp} sx={{ flex: 1, pt: 0.5, pb: 0.5 }}>
        {/* Section: Learning */}
        <Typography sx={glassStyles.sectionHeader(isDark)}>{t('nav.section.learning', 'Learning')}</Typography>
        <SidebarNavItem to="/subjects" icon={<MenuBookIcon sx={{ color: '#FF6F61' }} />} label={t('nav.subjects', 'Subjects')} onClick={handleNavClick} isDark={isDark} />
        {canSeePremiumMenus && (
          <SidebarNavItem to="/self-tests" icon={<FactCheckIcon sx={{ color: '#7E57C2' }} />} label={t('nav.selfTests', 'Self Test')} onClick={handleNavClick} isDark={isDark} />
        )}
        <SidebarNavItem to="/exam-papers" icon={<QuizIcon sx={{ color: '#E91E63' }} />} label={t('nav.examPapers', 'Exam Papers')} onClick={handleNavClick} isDark={isDark} />
        <SidebarNavItem to="/notes" icon={<NotesIcon sx={{ color: '#4DB6AC' }} />} label={t('nav.notes', 'Notes')} onClick={handleNavClick} isDark={isDark} />

        {/* Section: Social */}
        <Typography sx={glassStyles.sectionHeader(isDark)}>{t('nav.section.social', 'Social')}</Typography>
        <SidebarNavItem to="/posts" icon={<ArticleIcon sx={{ color: '#42A5F5' }} />} label={t('nav.posts', 'Posts')} onClick={handleNavClick} isDark={isDark} />
        {canSeePremiumMenus && (
          <SidebarNavItem to="/groups" icon={<GroupIcon sx={{ color: '#66BB6A' }} />} label={t('nav.groups', 'Groups')} onClick={handleNavClick} isDark={isDark} />
        )}
        <SidebarNavItem to="/blog" icon={<RssFeedIcon sx={{ color: '#42A5F5' }} />} label={t('nav.blog', 'Articles')} onClick={handleNavClick} isDark={isDark} />

        {/* Section: Tools */}
        <Typography sx={glassStyles.sectionHeader(isDark)}>{t('nav.section.tools', 'Tools')}</Typography>
        <SidebarNavItem to="/planning" icon={<CalendarMonthIcon sx={{ color: '#FFB347' }} />} label={t('nav.planning', 'Planning')} onClick={handleNavClick} isDark={isDark} />
        <SidebarNavItem to="/points" icon={<EmojiEventsIcon sx={{ color: '#FFD700' }} />} label={t('nav.points', 'Points')} onClick={handleNavClick} isDark={isDark} />
        <SidebarNavItem to="/call" icon={<VideoCallIcon sx={{ color: '#ef5350' }} />} label={t('call.startVideoCall', 'Video Call')} onClick={handleNavClick} isDark={isDark} />
      </List>

      {/* Footer — pinned to bottom */}
      <Box sx={{ flexShrink: 0, py: 1, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(102,126,234,0.5)', fontWeight: 600 }}>
          © {new Date().getFullYear()} EduMee.vn
        </Typography>
        <Typography sx={{ fontSize: 10, color: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(118,75,162,0.4)', mt: 0.3 }}>
          {t('sidebar.madeWithLove', 'Made with care for students')}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Desktop sidebar — permanent */}
      {isMdUp && (
        <Drawer
          variant="permanent"
          open
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              top: 0,
              height: '100vh',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              ...glassStyles.sidebar(isDark),
            }
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Mobile bottom sheet — "More" menu */}
      {!isMdUp && (
        <Drawer
          anchor="bottom"
          open={moreOpen}
          onClose={() => setMoreOpen(false)}
          ModalProps={{ disableScrollLock: true }}
          sx={{
            '& .MuiDrawer-paper': {
              borderRadius: '20px 20px 0 0',
              maxHeight: '70vh',
              background: isDark
                ? 'linear-gradient(180deg, rgba(30,41,59,0.98) 0%, rgba(17,24,39,0.99) 100%)'
                : 'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(249,250,251,0.98) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              paddingBottom: 'env(safe-area-inset-bottom)',
              boxShadow: isDark
                ? '0 -8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(102,126,234,0.1)'
                : '0 -8px 32px rgba(102,126,234,0.12), 0 0 0 1px rgba(99,102,241,0.08)',
              // Gradient accent line at top
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '10%',
                right: '10%',
                height: '3px',
                borderRadius: '0 0 4px 4px',
                background: 'linear-gradient(90deg, rgba(102,126,234,0.6) 0%, rgba(118,75,162,0.6) 100%)',
              },
            }
          }}
        >
          {/* Drag handle */}
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 0.5 }}>
            <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)' }} />
          </Box>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, pb: 1 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
              {t('nav.more', 'More')}
            </Typography>
            <IconButton size="small" onClick={() => setMoreOpen(false)} sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {/* Grouped sections */}
          {[
            {
              title: t('nav.section.social', 'Social'),
              items: [
                { to: '/chat', icon: <ChatBubbleOutlineIcon />, label: t('nav.chat', 'Chat'), color: '#6366f1' },
                { to: '/posts', icon: <ArticleIcon />, label: t('nav.posts', 'Posts'), color: '#42A5F5' },
                ...(canSeePremiumMenus ? [{ to: '/groups', icon: <GroupIcon />, label: t('nav.groups', 'Groups'), color: '#66BB6A' }] : []),
                { to: '/blog', icon: <RssFeedIcon />, label: t('nav.blog', 'Articles'), color: '#6366f1' },
              ],
            },
            {
              title: t('nav.section.tools', 'Tools'),
              items: [
                { to: '/notes', icon: <NotesIcon />, label: t('nav.notes', 'Notes'), color: '#4DB6AC' },
                { to: '/planning', icon: <CalendarMonthIcon />, label: t('nav.planning', 'Planning'), color: '#FFB347' },
                { to: '/calendar', icon: <CalendarMonthIcon />, label: t('calendar.title', 'Calendar'), color: '#0891b2' },
                { to: '/points', icon: <EmojiEventsIcon />, label: t('nav.points', 'Points'), color: '#FFD700' },
                { to: '/call', icon: <VideoCallIcon />, label: t('call.startVideoCall', 'Video Call'), color: '#ef5350' },
              ],
            },
          ].map((section) => (
            <Box key={section.title} sx={{ px: 2, pb: 1.5 }}>
              <Typography sx={{
                fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                color: isDark ? 'rgba(102,126,234,0.6)' : 'rgba(102,126,234,0.6)',
                px: 0.5, pb: 0.75,
              }}>
                {section.title}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5 }}>
                {section.items.map((item) => {
                  const isActive = location.pathname.startsWith(item.to);
                  return (
                    <Box
                      key={item.to}
                      component={RouterLink}
                      to={item.to}
                      onClick={() => setMoreOpen(false)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 1.5,
                        borderRadius: 3,
                        textDecoration: 'none',
                        transition: 'all 0.15s ease',
                        bgcolor: isActive
                          ? (isDark ? 'rgba(102,126,234,0.15)' : 'rgba(102,126,234,0.08)')
                          : 'transparent',
                        '&:active': {
                          bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                          transform: 'scale(0.95)',
                        },
                      }}
                    >
                      <Box sx={{
                        width: 44, height: 44,
                        borderRadius: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isDark ? `${item.color}20` : `${item.color}15`,
                        color: item.color,
                        mb: 0.5,
                        fontSize: 22,
                      }}>
                        {item.icon}
                      </Box>
                      <Typography sx={{
                        fontSize: 11,
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? '#667eea' : (isDark ? 'rgba(255,255,255,0.7)' : '#64748b'),
                        textAlign: 'center',
                        lineHeight: 1.2,
                      }}>
                        {item.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
          <Box sx={{ pb: 1 }} />
        </Drawer>
      )}

      <Main>
        {/* Unified Content Container - full width to match header */}
        {location.pathname !== '/' ? (
          <Box
            sx={{
              minHeight: 'calc(100vh - 64px)',
              position: 'relative',
              background: (t) => t.palette.mode === 'dark'
                ? 'linear-gradient(180deg, rgba(30,41,59,0.7) 0%, rgba(17,24,39,0.5) 200px, rgba(17,24,39,0.3) 100%)'
                : 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(249,250,251,0.95) 200px, rgba(245,247,250,0.9) 100%)',
              // Top accent line - seamless connection to header
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: (t) => t.palette.mode === 'dark'
                  ? 'linear-gradient(90deg, rgba(102,126,234,0.2) 0%, rgba(102,126,234,0.6) 20%, rgba(139,92,246,0.7) 50%, rgba(118,75,162,0.6) 80%, rgba(118,75,162,0.2) 100%)'
                  : 'linear-gradient(90deg, rgba(102,126,234,0.15) 0%, rgba(102,126,234,0.45) 20%, rgba(139,92,246,0.5) 50%, rgba(118,75,162,0.45) 80%, rgba(118,75,162,0.15) 100%)',
                boxShadow: (t) => t.palette.mode === 'dark'
                  ? '0 2px 20px rgba(102,126,234,0.4), 0 4px 40px rgba(139,92,246,0.25)'
                  : '0 2px 16px rgba(102,126,234,0.25), 0 4px 32px rgba(139,92,246,0.15)',
              },
              ...sx,
            }}
          >
            {/* Breadcrumb Section - full width like header */}
            {effectiveBreadcrumbs && effectiveBreadcrumbs.length > 0 && !(effectiveBreadcrumbs.length === 1 && !effectiveBreadcrumbs[0].to) && (
              <Box sx={{
                px: { xs: '12px', sm: '20px' }, // Match header padding exactly
                py: { xs: 1, sm: 1.25 },
                background: (t) => t.palette.mode === 'dark'
                  ? 'linear-gradient(180deg, rgba(102,126,234,0.1) 0%, rgba(102,126,234,0.03) 100%)'
                  : 'linear-gradient(180deg, rgba(102,126,234,0.06) 0%, rgba(102,126,234,0.02) 100%)',
                borderBottom: (t) => `1px solid ${t.palette.mode === 'dark' ? 'rgba(102,126,234,0.12)' : 'rgba(102,126,234,0.08)'}`,
              }}>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightRoundedIcon sx={{ fontSize: 18, color: 'rgba(139,92,246,0.5)' }} />}
                  sx={{
                    '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
                    '& .MuiBreadcrumbs-li': { whiteSpace: 'nowrap' },
                  }}
                >
                  {effectiveBreadcrumbs.map((bc, idx) => {
                    const isLast = !bc.to;
                    return isLast ? (
                      <Box key={idx} sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        px: 1.5, py: 0.4, borderRadius: 2,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: '#fff', fontWeight: 700, fontSize: { xs: '0.78rem', sm: '0.82rem' },
                        boxShadow: '0 2px 8px rgba(102,126,234,0.3)',
                      }}>
                        {bc.label}
                      </Box>
                    ) : (
                      <Link
                        key={idx}
                        component={RouterLink}
                        to={bc.to}
                        underline="none"
                        sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.5,
                          px: 1.25, py: 0.3, borderRadius: 1.5,
                          fontSize: { xs: '0.78rem', sm: '0.82rem' }, fontWeight: 600,
                          color: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : '#64748b',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(102,126,234,0.15)' : 'rgba(102,126,234,0.08)',
                            color: '#667eea',
                          }
                        }}
                      >
                        {idx === 0 && <HomeRoundedIcon sx={{ fontSize: 16 }} />}
                        {bc.label}
                      </Link>
                    );
                  })}
                </Breadcrumbs>
              </Box>
            )}
            {/* Main Content - full width, padding matches header */}
            <Box sx={{
              px: { xs: '12px', sm: '20px' }, // Match header padding exactly
              py: { xs: 1.5, sm: 2 },
            }}>
              {children}
            </Box>
          </Box>
        ) : (
          // Home page - no container wrapper needed
          children
        )}
      </Main>

      {/* Mobile Bottom Navigation */}
      {!isMdUp && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (t) => t.zIndex.appBar,
            borderRadius: 0,
            borderTop: '1px solid',
            borderColor: isDark ? 'rgba(102,126,234,0.15)' : 'rgba(99,102,241,0.1)',
            background: isDark
              ? 'linear-gradient(180deg, rgba(30,41,59,0.95) 0%, rgba(17,24,39,0.98) 100%)'
              : 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(249,250,251,0.95) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            boxShadow: isDark
              ? '0 -4px 20px rgba(0,0,0,0.3), 0 -1px 0 rgba(102,126,234,0.1)'
              : '0 -4px 20px rgba(102,126,234,0.08), 0 -1px 0 rgba(102,126,234,0.05)',
            // Gradient accent line at top
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: isDark
                ? 'linear-gradient(90deg, rgba(102,126,234,0.05) 0%, rgba(102,126,234,0.3) 30%, rgba(118,75,162,0.3) 70%, rgba(118,75,162,0.05) 100%)'
                : 'linear-gradient(90deg, rgba(102,126,234,0.1) 0%, rgba(102,126,234,0.35) 30%, rgba(118,75,162,0.35) 70%, rgba(118,75,162,0.1) 100%)',
              opacity: 0.7,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', height: BOTTOM_NAV_HEIGHT }}>
            {[
              { to: '/', icon: <HomeRoundedIcon />, label: t('nav.home', 'Home'), match: (p) => p === '/' },
              { to: '/subjects', icon: <MenuBookIcon />, label: t('nav.subjects', 'Subjects'), match: (p) => p.startsWith('/subjects') || p.startsWith('/lesson') },
              ...(canSeePremiumMenus ? [{ to: '/self-tests', icon: <FactCheckIcon />, label: t('nav.selfTests', 'Self Test'), match: (p) => p.startsWith('/self-tests') }] : []),
              { to: '/exam-papers', icon: <QuizIcon />, label: t('nav.examPapers', 'Exams'), match: (p) => p.startsWith('/exam-papers') || p.startsWith('/practice') },
            ].map((item) => {
              const isActive = item.match(location.pathname);
              return (
                <Box
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    py: 0.75,
                    textDecoration: 'none',
                    color: isActive
                      ? '#667eea'
                      : isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
                    transition: 'color 0.2s ease',
                    position: 'relative',
                    '&::before': isActive ? {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '25%',
                      right: '25%',
                      height: '3px',
                      borderRadius: '0 0 4px 4px',
                      background: 'linear-gradient(90deg, #667eea, #764ba2)',
                    } : {},
                  }}
                >
                  <Box sx={{
                    fontSize: 24,
                    display: 'flex',
                    transition: 'transform 0.2s ease',
                    transform: isActive ? 'scale(1.15)' : 'scale(1)',
                  }}>
                    {item.icon}
                  </Box>
                  <Typography sx={{
                    fontSize: 10,
                    fontWeight: isActive ? 700 : 500,
                    mt: 0.25,
                    lineHeight: 1.1,
                    maxWidth: '100%',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    px: 0.25,
                  }}>
                    {item.label}
                  </Typography>
                </Box>
              );
            })}
            {/* More tab — opens sidebar drawer */}
            <Box
              onClick={() => setMoreOpen(true)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                py: 0.75,
                cursor: 'pointer',
                color: moreOpen
                  ? '#667eea'
                  : isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
                transition: 'color 0.2s ease',
                position: 'relative',
                '&::before': moreOpen ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '25%',
                  right: '25%',
                  height: '3px',
                  borderRadius: '0 0 4px 4px',
                  background: 'linear-gradient(90deg, #667eea, #764ba2)',
                } : {},
              }}
            >
              <Box sx={{
                fontSize: 24,
                display: 'flex',
                transition: 'transform 0.2s ease',
                transform: moreOpen ? 'scale(1.15)' : 'scale(1)',
              }}>
                <MenuIcon />
              </Box>
              <Typography sx={{
                fontSize: 10,
                fontWeight: moreOpen ? 700 : 500,
                mt: 0.25,
                lineHeight: 1.1,
                textAlign: 'center',
              }}>
                {t('nav.more', 'More')}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Start Video Call: Create or Join Dialog */}
      <Dialog open={callDialogOpen} onClose={() => setCallDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('call.startVideoCall', 'Start Video Call')}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button size="small" variant={callMode === 'create' ? 'contained' : 'outlined'} onClick={() => setCallMode('create')}>
              {t('call.createMeeting', 'Create new meeting')}
            </Button>
            <Button size="small" variant={callMode === 'join' ? 'contained' : 'outlined'} onClick={() => setCallMode('join')}>
              {t('call.joinMeeting', 'Join existing')}
            </Button>
          </Box>
          {callMode === 'create' ? (
            <TextField
              autoFocus={!isTouchDevice()}
              fullWidth
              label={t('call.meetingGroupName', 'Meeting group name')}
              placeholder={t('call.meetingGroupPlaceholder', 'e.g., Study Group A')}
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
          ) : (
            <TextField
              autoFocus={!isTouchDevice()}
              fullWidth
              label={t('call.meetingRoomCode', 'Meeting room code')}
              placeholder={t('call.enterRoomCode', 'Enter room code')}
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCallDialogOpen(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          {callMode === 'create' ? (
            <Button onClick={handleCreateMeeting} variant="contained" disabled={!groupName.trim()}>
              {t('common.create', 'Create')}
            </Button>
          ) : (
            <Button onClick={handleJoinMeeting} variant="contained" disabled={!roomCode.trim()}>
              {t('call.join', 'Join')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
